import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  LinearProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';

const BudgetTracker = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetFormData, setBudgetFormData] = useState({
    year: new Date().getFullYear(),
    total_budget: '',
  });
  const [itemFormData, setItemFormData] = useState({
    description: '',
    amount: '',
    recipient: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
  });

  const fetchBudgets = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/budgets/?family_id=${currentFamily.id}`);
      setBudgets(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchBudgets();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchBudgets]);

  const handleCreateBudget = async () => {
    if (!currentFamily) {
      alert('Please select or create a family first');
      return;
    }
    try {
      const response = await api.post('/budgets/', {
        ...budgetFormData,
        family_id: currentFamily.id,
      });
      setBudgets([response.data, ...budgets]);
      setBudgetOpen(false);
      setBudgetFormData({
        year: new Date().getFullYear(),
        total_budget: '',
      });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create budget');
    }
  };

  const handleAddItem = async () => {
    try {
      await api.post(`/budgets/${selectedBudget.id}/add_item/`, itemFormData);
      fetchBudgets();
      setItemOpen(false);
      setItemFormData({
        description: '',
        amount: '',
        recipient: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
      });
    } catch (error) {
      alert('Failed to add item');
    }
  };

  const handleDeleteItem = async (budgetId, itemId) => {
    try {
      await api.delete(`/budgets/${budgetId}/items/${itemId}/`);
      fetchBudgets();
    } catch (error) {
      alert('Failed to delete item');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Budget Tracker</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setBudgetOpen(true)}
        >
          Create Budget
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to start tracking budgets.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : budgets.length === 0 ? (
        <Card>
          <CardContent>
            <Typography> Only you can see your budget. No budgets yet. Create your first budget!</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {budgets.map((budget) => {
            const percentage = (budget.spent / budget.total_budget) * 100;
            const remaining = budget.remaining;

            return (
              <Grid item xs={12} key={budget.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h5">{budget.year} Budget</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedBudget(budget);
                          setItemOpen(true);
                        }}
                      >
                        Add Expense
                      </Button>
                    </Box>
                    <Box mb={2}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">
                          Spent: ${parseFloat(budget.spent).toFixed(2)}
                        </Typography>
                        <Typography variant="body2">
                          Total: ${parseFloat(budget.total_budget).toFixed(2)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(percentage, 100)}
                        color={percentage > 100 ? 'error' : percentage > 80 ? 'warning' : 'primary'}
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Remaining: ${parseFloat(remaining).toFixed(2)}
                      </Typography>
                    </Box>
                    {budget.items?.length > 0 && (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Expenses
                        </Typography>
                        {budget.items.map((item) => (
                          <Box
                            key={item.id}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ py: 1, borderBottom: '1px solid #eee' }}
                          >
                            <Box>
                              <Typography variant="body1">{item.description}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.recipient && `${item.recipient} • `}
                                {item.category && `${item.category} • `}
                                {new Date(item.date).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" fontWeight="bold">
                                ${parseFloat(item.amount).toFixed(2)}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteItem(budget.id, item.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={budgetOpen}
        onClose={() => setBudgetOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Budget</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Year"
            type="number"
            margin="normal"
            value={budgetFormData.year}
            onChange={(e) =>
              setBudgetFormData({
                ...budgetFormData,
                year: parseInt(e.target.value),
              })
            }
          />
          <TextField
            fullWidth
            label="Total Budget ($)"
            type="number"
            margin="normal"
            value={budgetFormData.total_budget}
            onChange={(e) =>
              setBudgetFormData({
                ...budgetFormData,
                total_budget: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBudget} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={itemOpen}
        onClose={() => setItemOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Expense</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            value={itemFormData.description}
            onChange={(e) =>
              setItemFormData({ ...itemFormData, description: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Amount ($)"
            type="number"
            margin="normal"
            value={itemFormData.amount}
            onChange={(e) =>
              setItemFormData({ ...itemFormData, amount: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Recipient"
            margin="normal"
            value={itemFormData.recipient}
            onChange={(e) =>
              setItemFormData({ ...itemFormData, recipient: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Category"
            margin="normal"
            value={itemFormData.category}
            onChange={(e) =>
              setItemFormData({ ...itemFormData, category: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Date"
            type="date"
            margin="normal"
            value={itemFormData.date}
            onChange={(e) =>
              setItemFormData({ ...itemFormData, date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemOpen(false)}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BudgetTracker;
